import { useEffect, useState } from 'react';
import { getBasketCookie, removeBasketCookie } from 'shared-utils';

import type { BasketDetails } from 'shared-types/Basket';

type UseReservationBasketIdParams = {
  basketDetails?: BasketDetails;
  propertyId: number;
  language: string;
  fetchBasketDetails: (
    propertyId: number,
    basketId: number | string,
    language: string
  ) => Promise<unknown>;
};

type UseReservationBasketIdResult = {
  basketId: string | undefined;
  basketNotReady: boolean;
};

export function useReservationBasketId({
  basketDetails,
  propertyId,
  language,
  fetchBasketDetails,
}: UseReservationBasketIdParams): UseReservationBasketIdResult {
  const [staleBasketId, setStaleBasketId] = useState<string | undefined>(undefined);
  const cookieBasketId = getBasketCookie();
  const basketId = cookieBasketId && cookieBasketId !== staleBasketId ? cookieBasketId : undefined;
  const basketNotReady = Boolean(basketId) && !basketDetails;

  // Track which basket settled, not a boolean: a new cookie appearing after a
  // failed hydration must be fetched, not silently discarded.
  const [settledBasketId, setSettledBasketId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!basketId || basketDetails || settledBasketId === basketId) return undefined;

    // A failed hydration is handled by the cleanup effect; an unhandled
    // rejection must not escape. A completion for a superseded basket id
    // must not settle, or it would re-fire this effect into a duplicate fetch.
    let cancelled = false;
    Promise.resolve(fetchBasketDetails(propertyId, basketId, language))
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setSettledBasketId(basketId);
      });

    return () => {
      cancelled = true;
    };
  }, [basketId, basketDetails, settledBasketId, propertyId, language, fetchBasketDetails]);

  useEffect(() => {
    if (basketId && settledBasketId === basketId && !basketDetails) {
      removeBasketCookie();
      setStaleBasketId(basketId);
    }
  }, [settledBasketId, basketId, basketDetails]);

  return { basketId, basketNotReady };
}
